import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, StarIcon } from 'lucide-react';
import { whatsappLink } from '../lib/whatsapp';
import { useLanguage } from '../lib/i18n';
const slides = ['https://cdn.magicpatterns.com/uploads/nL4ZCZyQLycPbkJBqQHsD2/ChatGPT_Image_27_may_2026,_15_55_24.png', 'https://cdn.magicpatterns.com/uploads/8QFp18oWMs8MwanxMtZN9B/ChatGPT_Image_May_27,_2026_at_04_00_02_PM.png', 'https://cdn.magicpatterns.com/uploads/mgWAEMasuhkm1TjdwR8uuM/ChatGPT_Image_May_27,_2026_at_04_02_17_PM.png', 'https://cdn.magicpatterns.com/uploads/6eLwUfZGHeXUd9fEdv6jx9/ChatGPT_Image_May_27,_2026_at_04_04_13_PM.png', 'https://cdn.magicpatterns.com/uploads/hDBcH5Q95GLwyZaRQjUJ8c/ChatGPT_Image_May_27,_2026_at_04_44_31_PM.png'];
const AUTOPLAY_MS = 6000;
const scrollTo = (id: string) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
};
export function Hero() {
  const [index, setIndex] = useState(0);
  const {
    t,
    lang
  } = useLanguage();
  const goTo = useCallback((i: number) => {
    const len = slides.length;
    setIndex((i % len + len) % len);
  }, []);
  const next = useCallback(() => goTo(index + 1), [index, goTo]);
  const prev = useCallback(() => goTo(index - 1), [index, goTo]);
  useEffect(() => {
    const id = setInterval(() => setIndex(i => (i + 1) % slides.length), AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [index]);
  useEffect(() => {
    const img = new Image();
    img.src = slides[(index + 1) % slides.length];
  }, [index]);
  const handleDragEnd = (_: unknown, info: {
    offset: {
      x: number;
    };
  }) => {
    if (info.offset.x < -60) next();else if (info.offset.x > 60) prev();
  };
  return <section id="inicio" className="px-3 sm:px-6 lg:px-12 pt-3 sm:pt-6 pb-12 w-full max-w-[100vw] overflow-hidden" data-id="element-180">
      <div className="relative h-[75vh] min-h-[550px] lg:h-[85vh] overflow-hidden rounded-sm bg-volcanic w-full" data-id="element-181">
        <AnimatePresence mode="sync" data-id="element-182">
          <motion.div key={index} className="absolute inset-0" initial={{
          opacity: 0,
          scale: 1.05
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0
        }} transition={{
          opacity: {
            duration: 1.2,
            ease: 'easeInOut'
          },
          scale: {
            duration: 8,
            ease: 'easeOut'
          }
        }} drag="x" dragConstraints={{
          left: 0,
          right: 0
        }} dragElastic={0.15} onDragEnd={handleDragEnd} data-id="element-183">
            <img src={slides[index]} alt="Picnic romántico de lujo en Tenerife" className="w-full h-full object-cover pointer-events-none select-none" draggable={false} data-id="element-184" />
          </motion.div>
        </AnimatePresence>

        <div className="absolute inset-0 bg-volcanic/40 pointer-events-none" data-id="element-185" />
        <div className="absolute inset-0 bg-gradient-to-t from-volcanic/90 via-volcanic/20 to-transparent pointer-events-none" data-id="element-186" />

        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1.2,
        delay: 0.6,
        ease: 'easeOut'
      }} className="hidden sm:flex absolute top-8 left-6 sm:top-12 sm:left-12 items-center gap-3 bg-ivory/10 backdrop-blur-md border border-ivory/20 px-5 py-2.5 rounded-sm text-ivory text-[10px] sm:text-xs tracking-[0.2em] uppercase font-medium" data-id="element-187">
          <div className="flex gap-1" data-id="element-188">
            {Array.from({
            length: 5
          }).map((_, i) => <StarIcon key={i} size={12} className="fill-ivory text-ivory" data-id="element-189" />)}
          </div>
          <span data-id="element-190">{t('hero.exclusivity')}</span>
        </motion.div>

        <motion.div key={`copy-${index}`} initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1.2,
        delay: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute bottom-12 sm:bottom-20 left-5 right-5 sm:left-12 sm:right-auto max-w-3xl text-ivory z-10" data-id="element-191">
          <h2 className="font-display text-fluid-hero font-light mb-6 sm:mb-8" data-id="element-192">
            {t('hero.title1')} <br data-id="element-193" />
            <span className="font-script text-fluid-script-hero text-terracotta italic inline-block mt-[-0.2em]" data-id="element-194">
              {t('hero.title2')}
            </span>
          </h2>
          <p className="text-ivory/90 text-[13px] sm:text-base max-w-lg mb-8 sm:mb-10 leading-relaxed font-light hidden sm:block" data-id="element-195">
            {t('hero.desc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 w-full sm:w-auto" data-id="element-196">
            <a href={whatsappLink('hero', lang)} target="_blank" rel="noopener noreferrer" className="bg-terracotta hover:bg-terracottaDark transition-colors duration-300 px-8 sm:px-12 py-4 sm:py-4 rounded-md uppercase tracking-[0.2em] text-[11px] sm:text-xs text-center text-ivory w-full sm:w-auto" data-id="element-197">
              {t('hero.book')}
            </a>
            <button onClick={() => scrollTo('#experiencias')} className="border border-ivory/40 hover:bg-ivory hover:text-volcanic transition-colors duration-300 px-8 sm:px-12 py-4 sm:py-4 rounded-md uppercase tracking-[0.2em] text-[11px] sm:text-xs text-ivory w-full sm:w-auto" data-id="element-198">
              {t('hero.see')}
            </button>
          </div>
        </motion.div>

        <button onClick={prev} aria-label="Anterior" className="hidden md:flex absolute left-8 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-ivory/5 backdrop-blur-sm border border-ivory/20 items-center justify-center text-ivory hover:bg-ivory/20 transition-colors duration-300 z-10" data-id="element-199">
          <ChevronLeftIcon size={24} strokeWidth={1} data-id="element-200" />
        </button>
        <button onClick={next} aria-label="Siguiente" className="hidden md:flex absolute right-8 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-ivory/5 backdrop-blur-sm border border-ivory/20 items-center justify-center text-ivory hover:bg-ivory/20 transition-colors duration-300 z-10" data-id="element-201">
          <ChevronRightIcon size={24} strokeWidth={1} data-id="element-202" />
        </button>

        <div className="absolute top-6 right-5 sm:bottom-8 sm:top-auto sm:right-12 flex gap-2 sm:gap-3 z-10" data-id="element-203">
          {slides.map((_, i) => <button key={i} onClick={() => goTo(i)} aria-label={`Ir a slide ${i + 1}`} className={`h-[2px] transition-all duration-700 ${i === index ? 'w-8 sm:w-12 bg-terracotta' : 'w-4 sm:w-6 bg-ivory/40 hover:bg-ivory/80'}`} data-id="element-204" />)}
        </div>
      </div>
    </section>;
}